import { captureRequestError } from '@group-link-one/gl-utils';
import { isCancel } from 'axios';

/**
 * A request cancellation is not a real failure, so it must never reach Sentry.
 * Two shapes reach us:
 *   - axios `CanceledError` (code `ERR_CANCELED`) — from `CancelToken.cancel()`
 *     (e.g. the token-invalid guard on boot) or from aborting via `signal`
 *     (superseded list requests on filter/search change, unmount cleanup).
 *     `isCancel` covers both.
 *   - native `AbortError` — a bare `AbortController.abort()` not routed through
 *     axios. Guarded here for completeness.
 */
function isCancellation(error: unknown): boolean {
  return isCancel(error) || (error instanceof Error && error.name === 'AbortError');
}

interface ApiErrorBody {
  error?: string;
  message?: string;
  statusCode?: number;
}

function getResponse(error: unknown): { status?: number; data?: unknown } | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  return (error as { response?: { status?: number; data?: unknown } }).response;
}

/** Whether this is the single retry made after an `INVALID_TIMESTAMP` (see `useAxiosHTTPRequest`). */
function isClockRetry(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  return (error as { config?: { _clockRetried?: boolean } }).config?._clockRetried === true;
}

function getResponseData(response: { data?: unknown } | undefined): ApiErrorBody | undefined {
  const data = response?.data;
  return typeof data === 'object' && data !== null ? (data as ApiErrorBody) : undefined;
}

/**
 * Some BFF error shapes are expected, already-handled control flow rather than
 * failures worth alerting on — reporting them to Sentry is pure noise:
 *   - `INVALID_TIMESTAMP` on the first attempt — retried once after re-measuring
 *     the clock offset (see `handleInvalidTimestamp` in `useAxiosHTTPRequest`).
 *     If the retry is rejected too, the clock sync did not fix it: that one is
 *     reported.
 *   - Generic `Unauthorized` 401 ("Acesso Não Autorizado.") — already routed to
 *     the logout modal flow (see `apiPrivate`'s response interceptor). Gated on
 *     an actual 401 so the same body shape coming back with another status
 *     still gets reported.
 */
function isSuppressedApiError(
  data: ApiErrorBody | undefined,
  status: number | undefined,
  clockRetried: boolean,
): boolean {
  if (!data) return false;
  if (data.error === 'INVALID_TIMESTAMP') return !clockRetried;
  if (
    status === 401 &&
    (data.error === 'Unauthorized' || data.message === 'Acesso Não Autorizado.')
  )
    return true;
  return false;
}

/**
 * Report a failed request to Sentry. For axios errors this attaches the request
 * payload and response body (sensitive fields redacted, bodies truncated) as
 * dedicated context cards — see `captureRequestError` in gl-utils. Non-axios
 * errors fall through to a plain capture. Cancellations and known-expected API
 * error shapes (see `isSuppressedApiError`) are dropped.
 */
export function onRequestError(error: unknown): void {
  if (isCancellation(error)) return;

  const response = getResponse(error);
  if (isSuppressedApiError(getResponseData(response), response?.status, isClockRetry(error)))
    return;

  captureRequestError(error);
}
